package fr.manyverse.app.presentation.components.onboarding

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.pager.HorizontalPager
import androidx.compose.foundation.pager.rememberPagerState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Web
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.key.Key
import androidx.compose.ui.input.key.KeyEventType
import androidx.compose.ui.input.key.key
import androidx.compose.ui.input.key.onKeyEvent
import androidx.compose.ui.input.key.type
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.app_logo
import manyverse.shared.generated.resources.fig_mj
import manyverse.shared.generated.resources.fig_player
import manyverse.shared.generated.resources.fig_table
import manyverse.shared.generated.resources.fig_universes
import manyverse.shared.generated.resources.icon_bluesky
import manyverse.shared.generated.resources.icon_discord
import manyverse.shared.generated.resources.icon_instagram
import manyverse.shared.generated.resources.icon_youtube
import manyverse.shared.generated.resources.logo
import manyverse.shared.generated.resources.onboarding_community_description
import manyverse.shared.generated.resources.onboarding_community_title
import manyverse.shared.generated.resources.onboarding_gm_description
import manyverse.shared.generated.resources.onboarding_gm_title
import manyverse.shared.generated.resources.onboarding_link_bluesky
import manyverse.shared.generated.resources.onboarding_link_discord
import manyverse.shared.generated.resources.onboarding_link_instagram
import manyverse.shared.generated.resources.onboarding_link_site
import manyverse.shared.generated.resources.onboarding_link_youtube
import manyverse.shared.generated.resources.onboarding_player_description
import manyverse.shared.generated.resources.onboarding_player_title
import manyverse.shared.generated.resources.onboarding_subtitle
import manyverse.shared.generated.resources.onboarding_table_description
import manyverse.shared.generated.resources.onboarding_table_title
import manyverse.shared.generated.resources.onboarding_universe_description
import manyverse.shared.generated.resources.onboarding_universe_title
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@Composable
@Preview(widthDp = 412, heightDp = 924, showBackground = true, locale = "fr")
fun OnboardingCarousel(modifier: Modifier = Modifier) {
    val pagerState = rememberPagerState(pageCount = { 6 })
    val coroutineScope = rememberCoroutineScope()

    LaunchedEffect(pagerState) {
        while (true) {
            delay(5000)
            pagerState.animateScrollToPage((pagerState.currentPage + 1) % pagerState.pageCount)
        }
    }

    Column(
        modifier = modifier
            .focusable()
            .onKeyEvent { event ->
                if (event.type == KeyEventType.KeyDown) {
                    when (event.key) {
                        Key.DirectionLeft, Key.A, Key.Q -> {
                            if (pagerState.currentPage > 0) {
                                coroutineScope.launch {
                                    pagerState.animateScrollToPage(pagerState.currentPage - 1)
                                }
                                true
                            } else false
                        }

                        Key.DirectionRight, Key.D -> {
                            if (pagerState.currentPage < 5) {
                                coroutineScope.launch {
                                    pagerState.animateScrollToPage(pagerState.currentPage + 1)
                                }
                                true
                            } else false
                        }

                        else -> false
                    }
                } else false
            },
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        HorizontalPager(
            state = pagerState,
            modifier = Modifier.weight(1f).fillMaxWidth()
        ) { page ->
            OnboardingPage(pageIndex = page)
        }

        // Page indicators (clickable)
        Row(
            Modifier
                .fillMaxWidth()
                .padding(16.dp),
            horizontalArrangement = Arrangement.Center
        ) {
            repeat(6) { iteration ->
                val color = if (pagerState.currentPage == iteration) {
                    MaterialTheme.colorScheme.primary
                } else {
                    MaterialTheme.colorScheme.surfaceVariant
                }
                Box(
                    modifier = Modifier
                        .padding(4.dp)
                        .size(8.dp)
                        .background(color, CircleShape)
                        .clickable(
                            interactionSource = remember { MutableInteractionSource() },
                            indication = null
                        ) {
                            coroutineScope.launch {
                                pagerState.animateScrollToPage(iteration)
                            }
                        }
                )
            }
        }
    }
}

@Composable
private fun OnboardingPage(pageIndex: Int) {
    val uriHandler = LocalUriHandler.current

    Column(
        modifier = Modifier
            .fillMaxSize()
            .padding(24.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center
    ) {
        when (pageIndex) {
            0 -> {
                // Logo + subtitle
                Image(
                    painter = painterResource(Res.drawable.logo),
                    contentDescription = stringResource(Res.string.app_logo),
                    modifier = Modifier
                        .size(200.dp)
                        .padding(bottom = 24.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_subtitle),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    textAlign = TextAlign.Center
                )
            }

            1 -> {
                // Table / Group
                Image(
                    painter = painterResource(Res.drawable.fig_table),
                    contentDescription = stringResource(Res.string.onboarding_table_title),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(bottom = 24.dp),
                    contentScale = ContentScale.Fit
                )
                Text(
                    text = stringResource(Res.string.onboarding_table_title),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_table_description),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center
                )
            }

            2 -> {
                // GM Tools
                Image(
                    painter = painterResource(Res.drawable.fig_mj),
                    contentDescription = stringResource(Res.string.onboarding_gm_title),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(bottom = 24.dp),
                    contentScale = ContentScale.Fit
                )
                Text(
                    text = stringResource(Res.string.onboarding_gm_title),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_gm_description),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center
                )
            }

            3 -> {
                // Player Features
                Image(
                    painter = painterResource(Res.drawable.fig_player),
                    contentDescription = stringResource(Res.string.onboarding_player_title),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(bottom = 24.dp),
                    contentScale = ContentScale.Fit
                )
                Text(
                    text = stringResource(Res.string.onboarding_player_title),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_player_description),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center
                )
            }

            4 -> {
                // Universe Zero
                Image(
                    painter = painterResource(Res.drawable.fig_universes),
                    contentDescription = stringResource(Res.string.onboarding_universe_title),
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(200.dp)
                        .padding(bottom = 24.dp),
                    contentScale = ContentScale.Fit
                )
                Text(
                    text = stringResource(Res.string.onboarding_universe_title),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_universe_description),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center
                )
            }

            5 -> {
                // Community Links
                Text(
                    text = stringResource(Res.string.onboarding_community_title),
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    text = stringResource(Res.string.onboarding_community_description),
                    style = MaterialTheme.typography.bodyMedium,
                    textAlign = TextAlign.Center,
                    modifier = Modifier.padding(bottom = 32.dp)
                )

                Column(
                    verticalArrangement = Arrangement.spacedBy(16.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Button(
                        onClick = { uriHandler.openUri("https://manyverse.quest/") },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFF1976D2), // Blue
                            contentColor = Color.White
                        ),
                        modifier = Modifier.width(250.dp)
                    ) {
                        Icon(
                            imageVector = Icons.Default.Web,
                            contentDescription = null,
                            modifier = Modifier.padding(end = 8.dp)
                        )
                        Text(stringResource(Res.string.onboarding_link_site))
                    }

                    // Discord Button (Discord blurple)
                    Button(
                        onClick = { uriHandler.openUri("https://discord.gg/fUBK2r6wd4") },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFF5865F2), // Discord blurple
                            contentColor = Color.White
                        ),
                        modifier = Modifier.width(250.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .padding(end = 8.dp)
                                .size(24.dp)
                                .background(Color.White, CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.icon_discord),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                                tint = Color.Unspecified
                            )
                        }
                        Text(stringResource(Res.string.onboarding_link_discord))
                    }

                    // YouTube Button (YouTube red)
                    if (false) Button(
                        onClick = { uriHandler.openUri("https://www.youtube.com/@ManyVerse-Roleplay") },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFFFF0000), // YouTube red
                            contentColor = Color.White
                        ),
                        modifier = Modifier.width(250.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .padding(end = 8.dp)
                                .size(24.dp)
                                .background(Color.White, CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.icon_youtube),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                                tint = Color.Unspecified
                            )
                        }
                        Text(stringResource(Res.string.onboarding_link_youtube))
                    }

                    // Instagram Button (Instagram gradient approximated)
                    if (false) Button(
                        onClick = { uriHandler.openUri("https://www.instagram.com/manyverse_augmentedrp/") },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFFE1306C),
                            contentColor = Color.White
                        ),
                        modifier = Modifier.width(250.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .padding(end = 8.dp)
                                .size(24.dp)
                                .background(Color.White, CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.icon_instagram),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                                tint = Color.Unspecified
                            )
                        }
                        Text(stringResource(Res.string.onboarding_link_instagram))
                    }

                    // Bluesky Button
                    if (false) Button(
                        onClick = { uriHandler.openUri("https://bsky.app/profile/manyverse.bsky.social") },
                        colors = ButtonDefaults.buttonColors(
                            containerColor = Color(0xFF0085FF),
                            contentColor = Color.White
                        ),
                        modifier = Modifier.width(250.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .padding(end = 8.dp)
                                .size(24.dp)
                                .background(Color.White, CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                painter = painterResource(Res.drawable.icon_bluesky),
                                contentDescription = null,
                                modifier = Modifier.size(16.dp),
                                tint = Color.Unspecified
                            )
                        }
                        Text(stringResource(Res.string.onboarding_link_bluesky))
                    }
                }
            }
        }
    }
}
